import React, { useEffect, useState } from 'react';
import useLocalStorage from '@plug/hooks/use-local-storage';
import { MARY_KAY_OAUTH_CODE_VERIFIER } from '@plug/config/consts';
import { useMutation } from '@apollo/client';
import signinSsoMutation, {
  SignInWithSSOResponse,
  SignInWithSSOVariables,
} from '../../graphql/mutations/signin-sso-mutation';
import { useLogin } from '../../hooks/useLogin';
import { Flex } from 'rebass';
import { Typography } from '@lomadee/design-system';
import { Loading } from '@monitfy/ds';
import { useHistory } from 'react-router-dom';
import { SIGN_UP_PATH } from '../../routing';
import { Pages } from '../sign-up';
import { OrganizationRoleName } from '@plug/graphql/types';
import { SsoConflictModal } from './sso-conflict-modal';
import confirmSsoLink, {
  ConfirmSSOLinkResponse,
  ConfirmSSOLinkVariables,
} from '../../graphql/mutations/confirm-sso-link';
import { SSOButton } from './sso-button';

const LoginMaryKayConfirmation = () => {
  const { get } = useLocalStorage();
  const history = useHistory();
  const { login, loading: loginLoading } = useLogin();
  const [conflict, setConflict] = useState<
    SignInWithSSOResponse['signInWithSSO']['conflict']['items']
  >([]);

  const [consultantId, setConsultantId] = useState<string>();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [signIn, { loading, error }] = useMutation<SignInWithSSOResponse, SignInWithSSOVariables>(
    signinSsoMutation,
    {
      onCompleted(data) {
        if (data?.signInWithSSO?.conflict?.items?.length) {
          setConflict(data?.signInWithSSO?.conflict?.items);
          setConsultantId(data?.signInWithSSO?.conflict?.consultantId);
          setIsModalOpen(true);
          return;
        }

        return handleRedirect(
          data?.signInWithSSO?.data,
          data.signInWithSSO.organizationId,
          data?.signInWithSSO?.token,
        );
      },
    },
  );

  const [confirmLink] = useMutation<ConfirmSSOLinkResponse, ConfirmSSOLinkVariables>(
    confirmSsoLink,
    {
      onCompleted(data) {
        return handleRedirect(
          data?.confirmSSOLink?.data,
          data.confirmSSOLink.organizationId,
          data?.confirmSSOLink?.token,
        );
      },
    },
  );

  const getAccessToken = async () => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const codeVerifier = get(MARY_KAY_OAUTH_CODE_VERIFIER, { json: false }) as string;

    localStorage.removeItem(MARY_KAY_OAUTH_CODE_VERIFIER);

    signIn({
      variables: {
        input: {
          provider: 'MaryKay',
          code,
          codeVerifier,
        },
      },
    });
  };

  const isLoading = loading || loginLoading;
  const errors = error?.graphQLErrors?.length || 0;

  const handleRedirect = (
    data: SignInWithSSOResponse['signInWithSSO']['data'],
    organizationId: string,
    token?: string,
  ) => {
    if (token) {
      return login({
        id: data.user.id,
        token,
        email: data.user.email,
        username: data.user.username,
        cookieDays: 15,
        loginAnimation: true,
        redirect: true,
      });
    }

    if (data?.user?.email) {
      history.push({
        pathname: SIGN_UP_PATH.replace('/:hash?', ''),
        state: {
          page: Pages.SELLER,
          organizationId,
          ...(data && {
            email: data?.user.email,
            phone: data?.user.phone,
            username: data?.user.username,
            inviteHash: '',
            organizationName: 'Mary Kay',
            role: OrganizationRoleName.Member,
            document: data?.user?.document,
            customFields: data?.customFields,
            customDomain: window.location.hostname,
            step: 'BANK_INFORMATION' as CreateAccountContextISteps,
          }),
        },
      });
    }
  };

  useEffect(() => {
    getAccessToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Flex
      width="100vw"
      height="100vh"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      style={{ gap: '16px' }}
    >
      <Typography color="neutralLowDark" size="22" fontWeight={500}>
        Estamos sincronizando seus dados com nossa base...
      </Typography>

      {conflict?.length > 0 && (
        <SsoConflictModal
          conflicts={conflict}
          open={isModalOpen}
          onChooseAccount={userId =>
            confirmLink({
              variables: {
                input: {
                  userId,
                  consultantId,
                },
              },
            })
          }
        />
      )}

      {errors > 0 && <SSOButton />}

      {isLoading && <Loading />}
    </Flex>
  );
};

export default LoginMaryKayConfirmation;
